/* eslint-disable max-lines */
import {
  AdminTenantDetails,
  AdminTenantLocation,
  Camera,
  HakimoMonitoringStatus,
  MonitoringOptions,
  UpdateHakimoMonitoringPayload,
} from '@hakimo-ui/hakimo/types';
import { Cell, Column, Row, TableData } from '@hakimo-ui/hakimo/ui-table';
import { HakimoSpinner, Toggle } from '@hakimo-ui/shared/ui-base';
import { ChevronDoubleRightIcon } from '@heroicons/react/24/solid';

export const JENKINS_JOBS_URL = 'https://jenkins.hakimo.in/view/all/';

export type MonitoringTabId = 'tenants' | 'users';

export const MONITORING_TABS: { id: MonitoringTabId; name: string }[] = [
  { id: 'tenants', name: 'Monitoring SOCs' },
  { id: 'users', name: 'Users Access' },
];

export type MonitoringUsersAccessFilter = 'all' | 'enabled' | 'disabled';

export const MONITORING_USERS_ACCESS_FILTER_OPTIONS: {
  id: MonitoringUsersAccessFilter;
  label: string;
}[] = [
  { id: 'all', label: 'All' },
  { id: 'enabled', label: 'Enabled' },
  { id: 'disabled', label: 'Disabled' },
];

export function getMonitoringStatus(tenant: AdminTenantDetails): {
  enabled: boolean;
  label: string;
} {
  const enabledTenants = tenant.hakimoMonitoring?.enabledMonitoringTenants;
  const enabled = enabledTenants
    ? enabledTenants.length > 0
    : tenant.hakimoMonitoringEnabled;
  return {
    enabled,
    label: enabled ? 'Hakimo Monitoring Enabled' : 'Hakimo Monitoring Disabled',
  };
}

export const GRANT_TENANT_ACCESS_TOOLTIP_TEXT =
  'High-priority tenants are best handled exclusively by high-priority operators. To enforce that, uncheck the tenants above and remove this tenant from any tenant groups — otherwise regular operators will also receive its alarms.';

export const MONITORING_TENANTS: { id: string; name: string }[] = [
  { id: 'hakimo-india-soc', name: 'Hakimo SOC' },
  { id: 'keystone-security', name: 'Keystone Security' },
  { id: 'qhoh', name: 'Qhoh' },
];

export const HIGH_PRIORITY_OPERATOR_EMAILS = [
  'high_priority_operator_1@hakimo.ai',
  'high_priority_operator_2@hakimo.ai',
];

export function getMonitoringOptions(
  monitoring: HakimoMonitoringStatus | undefined
): MonitoringOptions {
  const tenantsFromPayload = monitoring?.allMonitoringTenants ?? [];
  const operatorsFromPayload = monitoring?.allHighPriorityOperatorEmails ?? [];
  return {
    allMonitoringTenants:
      tenantsFromPayload.length > 0 ? tenantsFromPayload : MONITORING_TENANTS,
    allHighPriorityOperatorEmails:
      operatorsFromPayload.length > 0
        ? operatorsFromPayload
        : HIGH_PRIORITY_OPERATOR_EMAILS,
    enabledOperatorEmails: new Set(
      monitoring?.enabledHighPriorityOperatorEmails ?? []
    ),
  };
}

export function getMonitoringUpdatePayload(
  tenantId: string,
  allMonitoringTenants: { id: string }[],
  allHighPriorityOperatorEmails: string[],
  selectedMonitoringTenants: Record<string, boolean>,
  selectedOperators: Record<string, boolean>,
  enabledOperatorEmails: Set<string>
): UpdateHakimoMonitoringPayload {
  const selectedTenantIds: string[] = [];
  const disabledTenantIds: string[] = [];
  allMonitoringTenants.forEach((t) => {
    (selectedMonitoringTenants[t.id]
      ? selectedTenantIds
      : disabledTenantIds
    ).push(t.id);
  });
  const selectedEmails: string[] = [];
  const disabledEmails: string[] = [];
  allHighPriorityOperatorEmails.forEach((email) => {
    if (selectedOperators[email]) selectedEmails.push(email);
    else if (enabledOperatorEmails.has(email)) disabledEmails.push(email);
  });
  if (selectedTenantIds.length === 0 && selectedEmails.length === 0) {
    return { tenantId, enabled: false };
  }
  return {
    tenantId,
    enabled: true,
    highPriorityOperatorEmails: selectedEmails,
    monitoringTenantIds: selectedTenantIds,
    disabledMonitoringTenantIds: disabledTenantIds,
    disabledOperatorEmails: disabledEmails,
  };
}

export const ENABLE_MONITORING_CHECKLIST = [
  { id: 'escalation', label: 'Is the escalation % under control?' },
  { id: 'cameraGrouping', label: 'Is camera grouping done?' },
  { id: 'floorplan', label: 'Is floorplan updated?' },
  { id: 'deadzones', label: 'Are deadzones updated?' },
  {
    id: 'twilio',
    label:
      'Is Twilio configuration done? For sending SMS, it should be added to campaign.',
  },
];

export const locationsColumns: Column[] = [
  { id: 'id', name: 'ID' },
  { id: 'name', name: 'Name' },
  { id: 'city', name: 'City' },
  { id: 'state', name: 'State' },
  { id: 'country', name: 'Country' },
  { id: 'actions', name: '' },
];

export function getLocationsTableData(
  locations: AdminTenantLocation[],
  onClickRow?: (
    locationId: string,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void
): TableData {
  return {
    columns: locationsColumns,
    rows: locations.map((location) => {
      const cells: Cell[] = [
        { value: location.id },
        { value: location.name },
        { value: location.city || '-' },
        { value: location.state || '-' },
        { value: location.country || '-' },
        {
          value: (
            <ChevronDoubleRightIcon className="flex h-5 w-5 flex-shrink-0 items-center justify-between" />
          ),
        },
      ];

      const rowData: Row = {
        id: location.id.toString(),
        cells,
        onClick: onClickRow
          ? (event) => onClickRow(location.id.toString(), event)
          : undefined,
      };

      return rowData;
    }),
  };
}

export const cameraColumns: Column[] = [
  { id: 'clientCameraId', name: 'Camera ID' },
  { id: 'name', name: 'Name' },
  { id: 'location', name: 'Location' },
  { id: 'image', name: 'Image' },
  { id: 'processVehicle', name: 'Vehicle Detection' },
  { id: 'maskedDeadzone', name: 'Masked Deadzone' },
];

export function isVehicleDetectionEnabled(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  camera: any
): boolean {
  return !!camera?.processVehicle;
}

export function isMaskedDeadzoneEnabled(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  camera: any
): boolean {
  return !!camera?.maskedDeadzoneEnabled;
}

export function getCameraTableData(
  cameras: (Camera & {
    clientCameraId?: string;
    processVehicle?: boolean;
    livestreamUrl?: string;
    maskedDeadzoneEnabled?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    camera_config?: any;
  })[],
  onToggleVehicleDetection: (cameraId: string, currentValue: boolean) => void,
  onClickImage: (
    camera: Camera & {
      clientCameraId?: string;
      processVehicle?: boolean;
      livestreamUrl?: string;
    }
  ) => void,
  loadingCameraId: string | null | undefined,
  onToggleMaskedDeadzone?: (cameraId: string, currentValue: boolean) => void,
  loadingMaskedDeadzoneCameraId?: string | null | undefined
): TableData {
  return {
    columns: cameraColumns,
    rows: cameras.map((camera) => {
      const processVehicle = isVehicleDetectionEnabled(camera);
      const maskedDeadzoneEnabled = isMaskedDeadzoneEnabled(camera);

      const cells: Cell[] = [
        { value: camera.clientCameraId || '-' },
        { value: camera.name || '-' },
        { value: camera.location?.name || '-' },
        {
          value: camera.imageUrl ? (
            <img
              src={camera.imageUrl}
              alt={camera.name}
              onClick={() => onClickImage(camera)}
              className="h-10 w-10 cursor-pointer rounded object-cover transition-opacity hover:opacity-80"
            />
          ) : (
            '-'
          ),
        },
        {
          value: (
            <div className="flex items-center justify-start gap-2">
              <Toggle
                enabled={processVehicle}
                onChange={() =>
                  onToggleVehicleDetection(camera.id, processVehicle)
                }
              />
              {loadingCameraId === camera.id && (
                <HakimoSpinner className="h-5 w-5" />
              )}
            </div>
          ),
        },
        {
          value: (
            <div className="flex items-center justify-start gap-2">
              <Toggle
                enabled={maskedDeadzoneEnabled}
                onChange={() =>
                  onToggleMaskedDeadzone?.(camera.id, maskedDeadzoneEnabled)
                }
              />
              {loadingMaskedDeadzoneCameraId === camera.id && (
                <HakimoSpinner className="h-5 w-5" />
              )}
            </div>
          ),
        },
      ];

      return {
        id: camera.id,
        cells,
      };
    }),
  };
}
